<script setup lang="ts">
/**
 * Step 4 · Look (plan §3 step 4, ui-spec §2.4): see it as it will be and choose how it looks.
 * Replaces Finish: no jobs, workers, renders or host. Material (from the kit; a stock change and
 * so a manufacturing change, said so), finish, fixings, name, books, environment, Inspect (the
 * live model exploded with part picking that round-trips to the cut list) and Save picture
 * (a browser snapshot of the live model).
 */
import { computed } from 'vue';
import { fmtMm } from '@/domain';
import { MATERIAL_CHOICES } from '@/families/stock';
import library from '../../assets/library/library.json';
import Icon from './Icon.vue';
import StepPanel from './StepPanel.vue';
import PanelHead from './PanelHead.vue';
import CutTable from './CutTable.vue';
import FixActions from './FixActions.vue';
import { app, snap, ui, viewerRef } from '@/app/state';
import { announce, toast } from '@/app/announce';
import { rename, setFinishTo, setHardwareShown, setMaterialTo, setProps, setStagingTo } from '@/app/edits';
import { savePicture } from '@/app/exports';
import { changeLabel, focusSoon, go, nextRevId } from '@/app/navigation';

const d = computed(() => snap.value.design);
const b = computed(() => snap.value.build);
const r = computed(() => snap.value.stockInfo);
const FINISHES = [
  { id: 'oiled', label: 'Oiled', note: 'Hard-wax oil: warm, low sheen' },
  { id: 'satin', label: 'Satin lacquer', note: 'Clear lacquer with a soft sheen' },
  { id: 'matt', label: 'Matt lacquer', note: 'Clear lacquer, no sheen' },
] as const;
/** The finish shown: the user's choice, else the stock's own (ply: matt lacquer; solid: oiled). */
const finish = computed(() => d.value.appearance.finish ?? (r.value.kind === 'board' ? 'oiled' : 'matt'));
const staging = computed(() => (d.value.appearance.staging === 'room' ? 'room' : 'studio'));
const glued = computed(() => b.value.parts.filter((p) => p.glueUp).length);
const nameDraft = computed(() => ('lookName' in ui.drafts ? ui.drafts.lookName! : d.value.appearance?.name ?? ''));
/** The kit's version (assets/library/library.json, owned by the Blender kit). */
const libraryVersion = String((library as { libraryVersion?: string }).libraryVersion ?? '–');

type Choice = (typeof MATERIAL_CHOICES)[number];
/** A material is chosen when the design's stock is its own or one of its thicker boards. */
const isChosen = (m: Choice) => d.value.stockProfileId === m.stockId || !!m.thicker?.some((t) => t.stockId === d.value.stockProfileId);
const chosen = computed(() => MATERIAL_CHOICES.find(isChosen) ?? null);
/** The chosen timber's thicknesses (FND-23: 25 / 38 / 45 mm sheesham, 20 / 38 / 45 mm oak). */
const thicknessOptions = computed(() => {
  const m = chosen.value;
  if (!m?.thicker?.length) return [];
  const base = /(\d+)\s*mm/.exec(m.note)?.[1] ?? /-(\d+)$/.exec(m.stockId)?.[1] ?? '';
  return [{ stockId: m.stockId, label: `${base} mm` }, ...m.thicker];
});
/** The swatch by species, so every stock of a timber (25, 38, 45 mm…) shows its own grain colour. */
function swatchOf(m: { id: string; stockId: string }): string {
  const s = `${m.id} ${m.stockId}`;
  return /sheesham/.test(s) ? 'sheesham' : /oak/.test(s) ? 'oak' : /birch|ply/.test(s) ? 'birch-ply' : m.id;
}
function pickMaterial(stockId: string) {
  focusSoon(`[data-material="${stockId}"]`);
  setMaterialTo(stockId);
}
function pickFinish(id: 'oiled' | 'satin' | 'matt') { focusSoon(`[data-finish="${id}"]`); setFinishTo(id); }
function pickStaging(s: 'studio' | 'room') {
  focusSoon(`[data-staging="${s}"]`);
  setStagingTo(s);
  viewerRef.value?.setOptions({ backdrop: s === 'room' });
}
/** Inspect: the kit's detailed pieces and part picking, on the assembled model (FND-15). */
function toggleInspect() {
  ui.inspect = !ui.inspect;
  viewerRef.value?.setOptions({ detail: ui.inspect });
  if (!ui.inspect) { ui.sel = null; viewerRef.value?.select(null); }
  announce(ui.inspect ? 'Inspecting the live model: detailed pieces. Select a part or a row to find it.' : 'Inspect off.');
}
/** Explode: pull the parts apart, with or without Inspect; never changes the design. */
function toggleExplode() {
  ui.explode = !ui.explode;
  viewerRef.value?.setOptions({ exploded: ui.explode });
  announce(ui.explode ? 'Parts pulled apart.' : 'Back to the assembled model.');
}
/** Open fronts: swing the doors, pull out the drawers; never changes the design. */
const hasFronts = computed(() => b.value.parts.some((p) => /^(door|door-stile|slider|drawer-front)$/.test(p.role)));
function toggleFronts() {
  ui.frontsOpen = !ui.frontsOpen;
  viewerRef.value?.setOptions({ frontsOpen: ui.frontsOpen });
  announce(ui.frontsOpen ? 'Doors open and drawers out.' : 'Doors and drawers closed.');
}
function onName(e: Event) {
  const el = e.target as HTMLInputElement;
  if (!el.isConnected) return;
  delete ui.drafts.lookName;
  rename(el.value);
}
function onProps(e: Event) { focusSoon('#lookPropsSw'); setProps((e.target as HTMLInputElement).checked); }
function onHardware(e: Event) { focusSoon('#hardwareSw'); setHardwareShown((e.target as HTMLInputElement).checked); }
async function picture() {
  focusSoon('[data-act="save-picture"]');
  await savePicture();
}
function commit() {
  const rv = app.store.commitRevision(changeLabel('Appearance'));
  toast(`Saved as ${rv.id}.`);
  announce(`Saved as revision ${rv.id}.`);
  focusSoon('#dock [data-go="pack"]');
}
</script>

<template>
  <StepPanel>
    <PanelHead :n="4" title="How it will look" lede="Nothing here changes what gets cut, except the material, which changes the stock and says so." />

    <section class="sec" aria-labelledby="matH">
      <h2 id="matH" class="h-sec">Material</h2>
      <div class="choices" role="radiogroup" aria-labelledby="matH">
        <button
          v-for="m in MATERIAL_CHOICES"
          :key="m.id"
          type="button"
          role="radio"
          class="choice"
          :aria-checked="isChosen(m)"
          :data-material="m.stockId"
          @click="pickMaterial(m.stockId)"
        >
          <span class="swatch" :class="`swatch--${swatchOf(m)}`" aria-hidden="true" />
          <span class="choice-t"><strong>{{ m.label }}</strong><small>{{ m.note }}</small></span>
        </button>
      </div>
      <div v-if="chosen?.thicker?.length" class="look-row look-row--thick">
        <span id="thickL" class="lbl">Board thickness</span>
        <div class="seg seg--sm" role="radiogroup" aria-labelledby="thickL">
          <button v-for="o in thicknessOptions" :key="o.stockId" type="button" role="radio" :aria-checked="d.stockProfileId === o.stockId" :data-thickness="o.stockId" @click="pickMaterial(o.stockId)">{{ o.label }}</button>
        </div>
      </div>
      <FixActions where="look" />
      <p class="hint" data-note="stock">Stock <span class="mono">{{ r.id }}</span>, {{ fmtMm(r.thicknessTicks) }} mm{{ glued ? ` · ${glued} parts glued up from strips` : '' }}. A different material changes the stock and the cut list; undo brings the previous one back.</p>
    </section>

    <section class="sec" aria-labelledby="finH">
      <h2 id="finH" class="h-sec">Finish <span class="muted">appearance only</span></h2>
      <div class="seg seg--sm" role="radiogroup" aria-labelledby="finH">
        <button v-for="f in FINISHES" :key="f.id" type="button" role="radio" :aria-checked="finish === f.id" :data-finish="f.id" :title="f.note" @click="pickFinish(f.id)">{{ f.label }}</button>
      </div>
      <p class="hint">{{ FINISHES.find((f) => f.id === finish)?.note }}. A finish never changes a size.</p>
    </section>

    <section class="sec" aria-labelledby="showH">
      <h2 id="showH" class="h-sec">On the model</h2>
      <div class="look-row"><label class="switch"><input id="hardwareSw" type="checkbox" role="switch" :checked="!!d.appearance.hardware" @change="onHardware"><span class="sw" aria-hidden="true" /><span>Show fixings</span></label></div>
      <div class="look-row"><label class="switch"><input id="lookPropsSw" type="checkbox" role="switch" :checked="d.appearance?.props !== false" @change="onProps"><span class="sw" aria-hidden="true" /><span>Show books and objects</span></label></div>
      <div class="look-row">
        <span id="envL" class="lbl">Environment</span>
        <div class="seg seg--sm" role="radiogroup" aria-labelledby="envL">
          <button type="button" role="radio" :aria-checked="staging === 'studio'" data-staging="studio" @click="pickStaging('studio')">Studio</button>
          <button type="button" role="radio" :aria-checked="staging === 'room'" data-staging="room" @click="pickStaging('room')">Room</button>
        </div>
      </div>
      <label class="lbl" for="lookName">Name</label>
      <input id="lookName" class="input" autocomplete="off" :value="nameDraft" @input="ui.drafts.lookName = ($event.target as HTMLInputElement).value" @change="onName">
    </section>

    <section class="sec" aria-labelledby="inspH">
      <div class="sec-head">
        <h2 id="inspH" class="h-sec">Inspect</h2>
        <div class="row-acts row-acts--tight">
          <button type="button" class="btn btn--sm" :class="ui.inspect ? 'btn--ink' : 'btn--quiet'" data-act="inspect" :aria-pressed="ui.inspect" @click="toggleInspect"><Icon name="eye" /> Inspect parts</button>
          <button type="button" class="btn btn--sm" :class="ui.explode ? 'btn--ink' : 'btn--quiet'" data-act="explode" :aria-pressed="ui.explode" @click="toggleExplode"><Icon name="explode" /> Explode</button>
          <button v-if="hasFronts" type="button" class="btn btn--sm" :class="ui.frontsOpen ? 'btn--ink' : 'btn--quiet'" data-act="fronts-open" :aria-pressed="ui.frontsOpen" @click="toggleFronts"><Icon name="explode" /> Open fronts</button>
        </div>
      </div>
      <p class="muted small">Inspect shows the detailed pieces and lets you pick a part; it is the same part in the cut list. Explode pulls the parts apart. Neither changes the design.</p>
      <div v-if="ui.inspect" class="table-wrap"><CutTable :build="b" compact caption="Parts of the live model" /></div>
    </section>

    <section class="sec" aria-labelledby="picH">
      <h2 id="picH" class="h-sec">Picture</h2>
      <button type="button" class="btn btn--quiet" data-act="save-picture" @click="picture"><Icon name="download" /> Save picture</button>
      <p class="muted small">A PNG of the model as you see it, named with the revision and manufacturing hash.<template v-if="ui.picture"> Last saved: <span class="mono">{{ ui.picture.filename }}</span>.</template></p>
      <p class="muted small" data-note="kit">Kit v{{ libraryVersion }} · materials from CC0 scans (see provenance).</p>
    </section>

    <template #dock>
      <button type="button" class="btn btn--quiet" data-act="commit" :disabled="!snap.isDirty" :title="snap.isDirty ? 'Freeze these edits as a new revision' : 'No unsaved edits'" @click="commit">
        <Icon name="save" /> {{ snap.isDirty ? `Save as ${nextRevId()}` : `${snap.currentRevisionId || 'Saved'} saved` }}
      </button>
      <button type="button" class="btn btn--ink btn--lg" data-go="pack" @click="go('pack')">Continue to Pack <Icon name="arrow" /></button>
    </template>
  </StepPanel>
</template>
